import React, { useEffect, useState, useCallback, useRef } from "react";
import { isNullOrUndefined, extend } from "@syncfusion/ej2-base";
import { L10n, setCulture } from "@syncfusion/ej2-base";
import enUsTrans from "./locales/en-us.json";
import zhCnTrans from "./locales/zh-cn.json";
import zhTWTrans from "./locales/zh-tw.json";
import jaTrans from "./locales/ja.json";
import { useTranslation } from "react-i18next";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import {
    MDBBtn,
    MDBSpinner,
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody,
    MDBModalFooter,
} from "mdb-react-ui-kit";
import {
    GridComponent,
    ColumnsDirective,
    ColumnDirective,
    Inject,
    Filter,
    Sort,
    Resize,
    Edit,
    Aggregate,
    AggregatesDirective,
    AggregateDirective,
    AggregateColumnsDirective,
    AggregateColumnDirective,
    Toolbar,
    ColumnChooser,
} from "@syncfusion/ej2-react-grids";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import "./DtaGrid.css";
import "./TimelineAutosave.css";
import { debounce } from "lodash";

const SCORE_OPTIONS = [
    { value: 0, text: "0", icon: "/dropdown-icons/cross.svg" },
    { value: 1, text: "1", icon: "/dropdown-icons/triangle-minus.svg" },
    { value: 2, text: "2", icon: "/dropdown-icons/triangle.svg" },
    { value: 3, text: "3", icon: "/dropdown-icons/triangle-plus.svg" },
    { value: 4, text: "4", icon: "/dropdown-icons/circle.svg" },
];

function getScoreOption(value) {
    const v = Number(value);
    return SCORE_OPTIONS.find((x) => x.value === v) || SCORE_OPTIONS[0];
}

function ScoreItemTemplate(data) {
    if (!data) return null;
    return (
        <div className="pugh-score-option" title={data.text}>
            <img className="pugh-score-icon" src={data.icon} alt={data.text} />
            <span className="pugh-score-text">{data.text}</span>
        </div>
    );
}

function makeScoreCellTemplate(field) {
    return (props) => {
        const option = getScoreOption(props?.[field]);
        return (
            <div className="pugh-score-cell" title={option.text}>
                <img className="pugh-score-icon" src={option.icon} alt={option.text} />
            </div>
        );
    };
}

// ====== Dialog Form Template ======
function DialogFormTemplate(props) {
    const nameRef = React.useRef(null);

    React.useEffect(() => {
        if (nameRef.current) {
            nameRef.current.focus();
        }
    }, []);

    const [val, setVal] = React.useState(extend({}, {}, props, true));

    const onChange = (args) => {
        const key = args.target.name;
        const value = args.target.value;
        setVal((prevVal) => ({ ...prevVal, [key]: value }));
    };

    let data = val;
    if (data.isAdd) {
        const keys = Object.keys(data);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (key !== "isAdd" && isNullOrUndefined(data[key])) {
                data[key] = "";
            }
        }
    }

    const dropdownOptions = SCORE_OPTIONS;

    return (
        <div>
            <div className="form-row">
                <div className="form-group col-md-12">
                    <div className="e-float-input e-control-wrapper">
                        <input
                            id="ID"
                            name="ID"
                            type="text"
                            disabled={true}
                            value={data.ID || ""}
                        />
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">ID</label>
                    </div>
                </div>
                <div className="form-group col-md-12">
                    <div className="e-float-input e-control-wrapper">
                        <textarea
                            ref={nameRef}
                            id="Name"
                            rows="4"
                            name="Name"
                            value={data.Name || ""}
                            onChange={onChange}
                        ></textarea>
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">Name</label>
                    </div>
                </div>
            </div>

            <div className="form-row">
                {/* Efficiency */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="Efficiency"
                        name="Efficiency"
                        value={parseInt(data.Efficiency) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Efficiency"
                        floatLabelType="Always"
                        change={(e) => setVal({ ...val, Efficiency: e.value })}
                    />
                </div>

                {/* ClarityToTheUser */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="ClarityToTheUser"
                        name="ClarityToTheUser"
                        value={parseInt(data.ClarityToTheUser) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Clarity To The User"
                        floatLabelType="Always"
                        change={(e) => setVal({ ...val, ClarityToTheUser: e.value })}
                    />
                </div>

                {/* RobustnessForAuditConformance */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="RobustnessForAuditConformance"
                        name="RobustnessForAuditConformance"
                        value={parseInt(data.RobustnessForAuditConformance) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Robustness For Audit Conformance"
                        floatLabelType="Always"
                        change={(e) =>
                            setVal({ ...val, RobustnessForAuditConformance: e.value })
                        }
                    />
                </div>

                {/* CostOrWaste */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="CostOrWaste"
                        name="CostOrWaste"
                        value={parseInt(data.CostOrWaste) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Cost Or Waste"
                        floatLabelType="Always"
                        change={(e) => setVal({ ...val, CostOrWaste: e.value })}
                    />
                </div>

                {/* ImplementationTiming */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="ImplementationTiming"
                        name="ImplementationTiming"
                        value={parseInt(data.ImplementationTiming) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Implementation Timing"
                        floatLabelType="Always"
                        change={(e) => setVal({ ...val, ImplementationTiming: e.value })}
                    />
                </div>

                {/* EngineeringCooperation */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="EngineeringCooperation"
                        name="EngineeringCooperation"
                        value={parseInt(data.EngineeringCooperation) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Engineering Cooperation"
                        floatLabelType="Always"
                        change={(e) => setVal({ ...val, EngineeringCooperation: e.value })}
                    />
                </div>

                {/* QualityAndManufacturingCooperation */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="QualityAndManufacturingCooperation"
                        name="QualityAndManufacturingCooperation"
                        value={parseInt(data.QualityAndManufacturingCooperation) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Quality And Manufacturing Cooperation"
                        floatLabelType="Always"
                        change={(e) =>
                            setVal({ ...val, QualityAndManufacturingCooperation: e.value })
                        }
                    />
                </div>

                {/* IsAllScCcIcMasterCreated */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="IsAllScCcIcMasterCreated"
                        name="IsAllScCcIcMasterCreated"
                        value={parseInt(data.IsAllScCcIcMasterCreated) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Is All SC/CC/IC Master Created"
                        floatLabelType="Always"
                        change={(e) =>
                            setVal({ ...val, IsAllScCcIcMasterCreated: e.value })
                        }
                    />
                </div>

                {/* IsAllPartInfoMasterCreated */}
                <div className="form-group col-md-6">
                    <DropDownListComponent
                        id="IsAllPartInfoMasterCreated"
                        name="IsAllPartInfoMasterCreated"
                        value={parseInt(data.IsAllPartInfoMasterCreated) || 0}
                        dataSource={dropdownOptions}
                        fields={{ text: "text", value: "value" }}
                        itemTemplate={ScoreItemTemplate}
                        valueTemplate={ScoreItemTemplate}
                        placeholder="Is All Part Info Master Created"
                        floatLabelType="Always"
                        change={(e) =>
                            setVal({ ...val, IsAllPartInfoMasterCreated: e.value })
                        }
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group col-md-12">
                    <div className="e-float-input e-control-wrapper">
                        <textarea
                            id="Note"
                            rows="4"
                            name="Note"
                            value={data.Note || ""}
                            onChange={onChange}
                        ></textarea>
                        <span className="e-float-line"></span>
                        <label className="e-float-text e-label-top">Note</label>
                    </div>
                </div>
            </div>
        </div>
    );
}

const defaultWeights = {
    Efficiency: 10,
    ClarityToTheUser: 10,
    RobustnessForAuditConformance: 10,
    CostOrWaste: 10,
    ImplementationTiming: 10,
    EngineeringCooperation: 10,
    QualityAndManufacturingCooperation: 10,
    IsAllScCcIcMasterCreated: 10,
    IsAllPartInfoMasterCreated: 10,
};

function PughGrid() {
    const { t } = useTranslation();
    const query = new URLSearchParams(window.location.search);
    const id = query.get("id") || null; // id 参数
    const tenantId = query.get("tenantId");
    L10n.load({
        "zh-CN": zhCnTrans,
        "zh-TW": zhTWTrans,
        en: enUsTrans,
        ja: jaTrans,
    });
    const [weights, setWeights] = useState(defaultWeights);

    const [gridData, setGridData] = useState();
    const [tempWeights, setTempWeights] = useState(null);

    function calculateOverall(data, appliedWeights) {
        const w = appliedWeights || weights;

        const sum =
            (Number(data.Efficiency) || 0) * (Number(w.Efficiency) || 0) +
            (Number(data.ClarityToTheUser) || 0) * (Number(w.ClarityToTheUser) || 0) +
            (Number(data.RobustnessForAuditConformance) || 0) *
            (Number(w.RobustnessForAuditConformance) || 0) +
            (Number(data.CostOrWaste) || 0) * (Number(w.CostOrWaste) || 0) +
            (Number(data.ImplementationTiming) || 0) *
            (Number(w.ImplementationTiming) || 0) +
            (Number(data.EngineeringCooperation) || 0) *
            (Number(w.EngineeringCooperation) || 0) +
            (Number(data.QualityAndManufacturingCooperation) || 0) *
            (Number(w.QualityAndManufacturingCooperation) || 0) +
            (Number(data.IsAllScCcIcMasterCreated) || 0) *
            (Number(w.IsAllScCcIcMasterCreated) || 0) +
            (Number(data.IsAllPartInfoMasterCreated) || 0) *
            (Number(w.IsAllPartInfoMasterCreated) || 0);

        return sum;
    }

    function updateRankAndSort(prevData, customWeights) {
        if (!prevData) return setGridData(prevData);

        const appliedWeights = customWeights || weights;

        // 步骤1：给每个项添加「原始索引」和「Overall」，保留原始顺序的标记
        const dataWithIndexAndOverall = prevData.map((item, originalIndex) => ({
            ...item,
            originalIndex, // 记录原始位置，用于后续恢复顺序
            Overall: calculateOverall(item, appliedWeights),
        }));

        // 步骤2：对所有项按Overall降序排序（仅用于计算Rank，不改变最终顺序）
        const sortedForRank = [...dataWithIndexAndOverall].sort((a, b) => {
            const overallA = Number(a.Overall) || 0;
            const overallB = Number(b.Overall) || 0;
            return overallB - overallA; // Overall大的排前面，方便算Rank
        });

        // 步骤3：生成「原始索引 → Rank」的映射表（核心：给每个原始项分配正确Rank）
        const rankMap = new Map();
        let currentRank = 1;
        sortedForRank.forEach((item, index) => {
            if (index > 0 && item.Overall === sortedForRank[index - 1].Overall) {
                // overall相同，Rank与前一个相同
                rankMap.set(item.originalIndex, rankMap.get(sortedForRank[index - 1].originalIndex));
            } else {
                rankMap.set(item.originalIndex, currentRank);
            }
            currentRank++;
        });

        // 步骤4：按「原始索引」恢复原始顺序，并赋值Rank
        const updatedData = dataWithIndexAndOverall
            .sort((a, b) => a.originalIndex - b.originalIndex) // 强制恢复原始顺序
            .map((item) => ({
                ...item,
                Rank: rankMap.get(item.originalIndex), // 取计算好的Rank
                originalIndex: undefined, // 移除临时的原始索引，不污染最终数据
            }));
        setGridData(updatedData);
    }

    const saveGridDataToDB = useCallback(
        debounce(async (data, currentWeights) => {
            if (!data || !Array.isArray(data)) return;
            try {
                const response = await fetch("/api/v1/PlugExecel", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        tenantId: tenantId,
                        model: {
                            rows: data,
                            weights: currentWeights,
                        },
                        jobId: id,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`保存失败：${response.statusText}`);
                }

                const result = await response.json();
                console.log("数据保存成功：", result);
                // 可选：提示用户保存成功（如message组件）
                // message.success('数据保存成功！');
            } catch (error) {
                console.error("数据保存失败：", error);
                // 可选：提示用户保存失败
                // message.error(saveError);
            } finally {
            }
        }, 500), // 500ms防抖
        [tenantId, id]
    );

    const [previewUrl, setPreviewUrl] = useState(null);
    const preview = async () => {
        try {
            setPreviewLoading(true);

            // 等待当前防抖保存完成（如果存在挂起的保存调用）
            let pendingSavePromise = null;
            if (saveGridDataToDB && typeof saveGridDataToDB.flush === "function") {
                pendingSavePromise = saveGridDataToDB.flush();
            }
            if (pendingSavePromise && typeof pendingSavePromise.then === "function") {
                await pendingSavePromise;
            }

            setImageLoaded(false);
            setShowModal(true);
            setPreviewUrl(null);


            const response = await fetch(
                `/api/v1/Preview?id=${id}&tenantId=${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`失败：${response.statusText}`);
            }

            const result = await response.json();
            setPreviewUrl(result.result.url);
        } catch (error) {
            console.error("Preview failed:", error);
        } finally {
            setPreviewLoading(false);
        }
    };

    const [loading, setLoading] = useState(false);
    const gridInstance = useRef(null);

    useEffect(() => {
        // 防重复请求：仅当数据有Rank字段（说明是更新后的有效数据）且长度>0时保存
        if (gridData) {
            saveGridDataToDB(gridData, weights);
        }
    }, [gridData, weights, saveGridDataToDB]);

    useEffect(() => {
        updateRankAndSort(gridData);
    }, []);

    useEffect(() => {
        async function fetchData() {
            try {
                if (!id) {
                    return;
                }
                // 开始 loading
                setLoading(true);
                const resp = await fetch(
                    `/api/v1/PlugExecel?id=${id}&tenantId=${tenantId}`
                );
                const data = await resp.json();
                if (data.result.data) {
                    const raw = JSON.parse(data.result.data);
                    let rows = [];
                    let loadedWeights = null;

                    if (Array.isArray(raw)) {
                        // 旧数据格式：直接是行数组
                        rows = raw;
                    } else if (raw && Array.isArray(raw.Rows)) {
                        // 新数据格式：{ rows: [...], weights: {...} }
                        rows = raw.Rows;
                        if (raw.Weights) {
                            loadedWeights = { ...defaultWeights, ...raw.Weights };
                        }
                    }

                    if (loadedWeights) {
                        setWeights(loadedWeights);
                    }

                    const dataWithId = (rows || []).map((item, index) => ({
                        ...item,
                        ID: index + 1,
                    }));

                    updateRankAndSort(dataWithId);
                }
            } catch (e) {
                setGridData([]);
                console.error(e);
                // 出错时可以 fallback 到默认
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [id, tenantId]);

    useEffect(() => {
        setCulture(localStorage.getItem("i18nextLng"));
    }, [gridInstance.current]);

    const editSettings = {
        allowEditing: true,
        allowAdding: true,
        showAddNewRow: false,
        allowDeleting: true,
        newRowPosition: "Bottom",
        mode: "Dialog",
        template: dialogTemplate,
    };

    function actionBegin(args) {
        if (args.requestType === "save") {
            //args.index = args.data.ID;
            if (
                gridInstance.current.pageSettings.currentPage !== 1 &&
                gridInstance.current.editSettings.newRowPosition === "Top"
            ) {
                args.index =
                    gridInstance.current.pageSettings.currentPage *
                    gridInstance.current.pageSettings.pageSize -
                    gridInstance.current.pageSettings.pageSize;
            } else if (
                gridInstance.current.editSettings.newRowPosition === "Bottom"
            ) {
                args.index =
                    gridInstance.current.pageSettings.currentPage *
                    gridInstance.current.pageSettings.pageSize -
                    1;
            }
        } else if (args.requestType === "add") {
            // ✅ 使用正确的字段名初始化新行
            args.rowData = {
                ID: (gridData?.length || 0) + 1,
                Name: "",
                Note: "",
                Efficiency: 0,
                ClarityToTheUser: 0,
                RobustnessForAuditConformance: 0,
                CostOrWaste: 0,
                ImplementationTiming: 0,
                EngineeringCooperation: 0,
                QualityAndManufacturingCooperation: 0,
                IsAllScCcIcMasterCreated: 0,
                IsAllPartInfoMasterCreated: 0,
                Overall: 0,
                Rank: 0,
            };
        }
    }

    const actionComplete = (args) => {
        if (args.requestType === "delete") {
            setTimeout(() => {
                updateRankAndSort(gridInstance.current.dataSource, weights);
            }, 500);
            //updateRankAndSort(gridInstance.current.dataSource, weights);
        } else if (args.requestType === "save") {
            setTimeout(() => {
                updateRankAndSort(gridInstance.current.dataSource, weights);
            }, 500);
        }
    };

    const toolbarOptions = [
        "Add",
        "Edit",
        "Delete",
        t("Preview"),
        t("ModifyWeights"),
    ];
    const validationRules = { required: true };

    function toolbarClick(args) {
        if (args.item.text === "Add" && gridData && gridData.length >= 100) {
            alert("Maximum 100 items allowed");
            args.cancel = true;
        }
        if (args.item.text === t("Preview")) {
            if (previewLoading) {
                args.cancel = true;
                return;
            }
            preview();
        }
        if (args.item.text === t("ModifyWeights")) {
            setTempWeights(weights);
            setShowWeightModal(true);
        }
    }

    const [showWeightModal, setShowWeightModal] = useState(false);

    function footerSum(props) {
        return <div>{props.Sum}</div>;
    }

    function dialogTemplate(props) {
        return <DialogFormTemplate {...props} />;
    }

    function HeadTemplate(props) {
        const weightMap = {
            Efficiency: weights.Efficiency,
            ClarityToTheUser: weights.ClarityToTheUser,
            RobustnessForAuditConformance: weights.RobustnessForAuditConformance,
            CostOrWaste: weights.CostOrWaste,
            ImplementationTiming: weights.ImplementationTiming,
            EngineeringCooperation: weights.EngineeringCooperation,
            QualityAndManufacturingCooperation:
                weights.QualityAndManufacturingCooperation,
            IsAllScCcIcMasterCreated: weights.IsAllScCcIcMasterCreated,
            IsAllPartInfoMasterCreated: weights.IsAllPartInfoMasterCreated,
        };

        const weight = weightMap[props.field];

        return (
            <div
                style={{ whiteSpace: "normal", lineHeight: "1.5" }}
                className="small"
            >
                <div>{props.headerText}</div>
                {weight !== undefined && (
                    <div
                        style={{
                            color: "#999",
                            marginTop: 2,
                        }}
                    >
                        {t("Weight")}: {weight}
                    </div>
                )}
            </div>
        );
    }

    const [showModal, setShowModal] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);

    return (
        <div className="control-pane">
            <div className="autosave-indicator">
                {t("Autosave")}
            </div>
            {
                previewLoading && (
                    <div
                        className="diagram-loading-indicator"
                        style={{
                            position: "absolute",
                            placeSelf: "anchor-center",
                            textAlign: "center",
                            top: 10,
                            right: 10,
                            zIndex: 10,
                        }}>
                        <MDBSpinner grow color="primary">
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                        <MDBSpinner grow className="ms-2" color="dark">
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                        <MDBSpinner grow className="mx-2" color="secondary">
                            <span className="visually-hidden">Loading...</span>
                        </MDBSpinner>
                    </div>
                )
            }
            {
                showModal && previewUrl && (
                    <Lightbox
                        key={showModal}
                        mainSrc={previewUrl}
                        onCloseRequest={() => {
                            setShowModal(false);
                            setPreviewUrl(null);
                            setImageLoaded(false);
                        }}
                        clickOutsideToClose={false}
                        reactModalStyle={{
                            overlay: {
                                zIndex: 1400,
                            },
                        }}
                        onImageLoad={() => {
                            setImageLoaded(true);
                        }}
                        onAfterOpen={() => { }}
                        onImageLoadError={() => { }}
                        imageLoadErrorMessage={() => { }}
                        loader={
                            <div style={{ display: imageLoaded ? "none" : "block" }}>
                                ...
                            </div>
                        }
                        imageTitle={null}
                        imageCaption={null}
                    />
                )
            }

            <MDBModal open={showWeightModal} tabIndex="-1" staticBackdrop>
                <MDBModalDialog centered>
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>{t("ModifyWeights")}</MDBModalTitle>
                        </MDBModalHeader>
                        <MDBModalBody className="fs-5">
                            {tempWeights && (
                                <div className="container-fluid">
                                    <div className="row mb-2">
                                        <div className="col-8">Efficiency</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.Efficiency}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        Efficiency: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8">Clarity To The User</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.ClarityToTheUser}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        ClarityToTheUser: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8 form-label">
                                            Robustness For Audit Conformance
                                        </div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.RobustnessForAuditConformance}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        RobustnessForAuditConformance: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8">Cost / Waste</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.CostOrWaste}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        CostOrWaste: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8">Implementation Timing</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.ImplementationTiming}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        ImplementationTiming: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8">Engineering Cooperation</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.EngineeringCooperation}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        EngineeringCooperation: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8">
                                            Quality & Manufacturing Cooperation
                                        </div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.QualityAndManufacturingCooperation}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        QualityAndManufacturingCooperation: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-8">All SC/CC/IC Master Created</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.IsAllScCcIcMasterCreated}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        IsAllScCcIcMasterCreated: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-8">All Part Info Master Created</div>
                                        <div className="col-4">
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                className="form-control"
                                                value={tempWeights.IsAllPartInfoMasterCreated}
                                                onChange={(e) => {
                                                    let v = Number(e.target.value);
                                                    if (v > 10) v = 10;
                                                    if (v < 0 || isNaN(v)) v = 0;
                                                    setTempWeights({
                                                        ...tempWeights,
                                                        IsAllPartInfoMasterCreated: v,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <div className="text-center w-100">
                                <MDBBtn
                                    type="button"
                                    color="light"
                                    onClick={() => {
                                        setShowWeightModal(false);
                                    }}
                                >
                                    {t("Cancel")}
                                </MDBBtn>
                                <MDBBtn
                                    type="button"
                                    onClick={() => {
                                        if (tempWeights) {
                                            setWeights(tempWeights);
                                            if (gridData && gridData.length > 0) {
                                                updateRankAndSort(gridData, tempWeights);
                                            }
                                        }
                                        setShowWeightModal(false);
                                    }}
                                >
                                    {t("OK")}
                                </MDBBtn>
                            </div>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>

            <div className="control-section vh-100">
                {
                    loading && (
                        <div
                            className="diagram-loading-indicator"
                            style={{
                                position: "absolute",
                                placeSelf: "anchor-center",
                                top: "calc(50% - 24px)",
                                right: "calc(50% - 75px)",
                                zIndex: 10,
                            }}>
                            <MDBSpinner grow color="primary">
                                <span className="visually-hidden">Loading...</span>
                            </MDBSpinner>
                            <MDBSpinner grow className="ms-2" color="dark">
                                <span className="visually-hidden">Loading...</span>
                            </MDBSpinner>
                            <MDBSpinner grow className="mx-2" color="secondary">
                                <span className="visually-hidden">Loading...</span>
                            </MDBSpinner>
                        </div>
                    )
                }

                <GridComponent
                    locale={localStorage.getItem("i18nextLng")}
                    dataSource={gridData}
                    toolbar={toolbarOptions}
                    editSettings={editSettings}
                    ref={(grid) => (gridInstance.current = grid)}
                    actionBegin={actionBegin}
                    actionComplete={actionComplete}
                    toolbarClick={toolbarClick}
                    clipMode="EllipsisWithTooltip"
                    rowHeight={60}
                    allowTextWrap={true}
                >
                    <ColumnsDirective>
                        <ColumnDirective
                            field="ID"
                            headerText="ID"
                            width="60"
                            textAlign="Center"
                            allowEditing={false}
                            isPrimaryKey={true}
                        />
                        <ColumnDirective
                            field="Name"
                            width="150"
                            validationRules={validationRules}
                        />
                        <ColumnDirective
                            field="Efficiency"
                            headerText="Efficiency"
                            width="100"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("Efficiency")}
                        />
                        <ColumnDirective
                            field="ClarityToTheUser"
                            headerText="Clarity To The User"
                            width="120"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("ClarityToTheUser")}
                        />
                        <ColumnDirective
                            field="RobustnessForAuditConformance"
                            headerText="Robustness For Audit Conformance"
                            width="180"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("RobustnessForAuditConformance")}
                        />
                        <ColumnDirective
                            field="CostOrWaste"
                            headerText="Cost / Waste"
                            width="100"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("CostOrWaste")}
                        />
                        <ColumnDirective
                            field="ImplementationTiming"
                            headerText="Implementation Timing"
                            width="120"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("ImplementationTiming")}
                        />
                        <ColumnDirective
                            field="EngineeringCooperation"
                            headerText="Engineering Cooperation"
                            width="140"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("EngineeringCooperation")}
                        />
                        <ColumnDirective
                            field="QualityAndManufacturingCooperation"
                            headerText="Quality & Manufacturing Cooperation"
                            width="180"
                            editType="dropdownedit"
                            validationRules={validationRules}
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate(
                                "QualityAndManufacturingCooperation"
                            )}
                        />
                        <ColumnDirective
                            field="IsAllScCcIcMasterCreated"
                            headerText="All SC/CC/IC Master Created"
                            width="140"
                            editType="dropdownedit"
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("IsAllScCcIcMasterCreated")}
                        />
                        <ColumnDirective
                            field="IsAllPartInfoMasterCreated"
                            headerText="All Part Info Master Created"
                            width="140"
                            editType="dropdownedit"
                            headerTemplate={HeadTemplate}
                            template={makeScoreCellTemplate("IsAllPartInfoMasterCreated")}
                        />
                        <ColumnDirective
                            field="Overall"
                            headerText="Overall"
                            width="80"
                            allowEditing={false}
                            headerTemplate={HeadTemplate}
                        />
                        <ColumnDirective
                            field="Rank"
                            headerText="Rank"
                            width="70"
                            allowEditing={false}
                            headerTemplate={HeadTemplate}
                        />
                        <ColumnDirective field="Note" headerText="Note" width="150" />
                    </ColumnsDirective>

                    {/* 聚合：只保留一个 AggregatesDirective */}
                    <AggregatesDirective>
                        <AggregateDirective>
                            <AggregateColumnsDirective>
                                <AggregateColumnDirective
                                    field="Overall"
                                    type="Sum"
                                    footerTemplate={footerSum}
                                    format="N0"
                                />
                            </AggregateColumnsDirective>
                        </AggregateDirective>
                    </AggregatesDirective>

                    <Inject
                        services={[
                            Filter,
                            Aggregate,
                            Edit,
                            Sort,
                            Resize,
                            Toolbar,
                            ColumnChooser,
                        ]}
                    />
                </GridComponent>
            </div>
        </div>
    );
}

export default PughGrid;
